'use client';

import React, { ReactNode, useEffect, useRef } from 'react';
import { useVideoProtection } from '../../hooks/useVideoProtection';
import { DynamicWatermark } from './dynamic-watermark';

interface VideoProtectionProps {
    children: ReactNode;
    watermarkText?: string;
    onRecordingDetected?: () => void;
    pauseOnRecording?: boolean;
    videoRef?: React.RefObject<HTMLVideoElement | null>;
    showWarning?: boolean;
    enableDynamicWatermark?: boolean;
}

export const VideoProtection: React.FC<VideoProtectionProps> = ({
    children,
    watermarkText = '',
    onRecordingDetected,
    pauseOnRecording = true,
    videoRef,
    showWarning = true,
    enableDynamicWatermark = true
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [showRecordingWarning, setShowRecordingWarning] = React.useState(false);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { suspiciousActivities } = useVideoProtection({
        videoRef,
        logActivity: true,
        onSuspiciousActivity: (activity) => {
            console.warn('⚠️ Suspicious activity:', activity);

            if (activity.includes('ضبط صفحه‌نمایش') || activity.includes('iOS')) {
                setShowRecordingWarning(true);

                if (pauseOnRecording && videoRef?.current) {
                    videoRef.current.pause();
                }

                if (onRecordingDetected) {
                    onRecordingDetected();
                }
            }
        }
    });

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const preventContextMenu = (e: MouseEvent) => {
            e.preventDefault();
            return false;
        };

        const preventDrag = (e: DragEvent) => {
            e.preventDefault();
            return false;
        };

        const preventScreenshot = (e: KeyboardEvent) => {
            if (e.key === 'PrintScreen') {
                e.preventDefault();
                navigator.clipboard.writeText('');
            }

            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 's') {
                e.preventDefault();
            }

            if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
                e.preventDefault();
            }
        };

        const preventSelection = (e: Event) => {
            e.preventDefault();
            return false;
        };

        container.addEventListener('contextmenu', preventContextMenu);
        container.addEventListener('dragstart', preventDrag);
        container.addEventListener('selectstart', preventSelection);
        document.addEventListener('keydown', preventScreenshot);

        const disableDevTools = () => {
            if (window.outerWidth - window.innerWidth > 160 || window.outerHeight - window.innerHeight > 160) {
                if (videoRef?.current) {
                    videoRef.current.pause();
                }
            }
        };

        const devToolsInterval = setInterval(disableDevTools, 1000);

        return () => {
            container.removeEventListener('contextmenu', preventContextMenu);
            container.removeEventListener('dragstart', preventDrag);
            container.removeEventListener('selectstart', preventSelection);
            document.removeEventListener('keydown', preventScreenshot);
            clearInterval(devToolsInterval);
        };
    }, [videoRef]);

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
            }}
            data-no-screenshot='true'
        >
            {children}

            {watermarkText && enableDynamicWatermark && <DynamicWatermark text={watermarkText} />}

            {showWarning && showRecordingWarning && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                        gap: '1rem',
                        padding: '2rem'
                    }}
                >
                    <div style={{ fontSize: '4rem' }}>🚫</div>
                    <h2 style={{ color: '#ff4444', fontSize: '1.5rem', textAlign: 'center', margin: 0 }}>
                        ضبط صفحه‌نمایش شناسایی شد
                    </h2>
                    <p style={{ color: 'white', fontSize: '1rem', textAlign: 'center', maxWidth: '400px' }}>
                        ضبط صفحه‌نمایش این محتوا مجاز نیست. لطفاً ضبط صفحه را متوقف کنید.
                    </p>
                    <p style={{ color: '#ff9999', fontSize: '0.875rem', textAlign: 'center' }}>
                        Screen recording is not allowed for this content.
                    </p>
                </div>
            )}

            <style jsx>{`
                div[data-no-screenshot] {
                    -webkit-touch-callout: none;
                    -webkit-user-select: none;
                    -khtml-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    user-select: none;
                }

                div[data-no-screenshot] video {
                    pointer-events: auto;
                }

                div[data-no-screenshot]::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    pointer-events: none;
                    z-index: 1;
                }
            `}</style>
        </div>
    );
};

